import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from '@react-google-maps/api';
import { Person, TrendingUp, LocationOn, HelpOutline, Email, Phone, CalendarToday } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { ENV_CONFIG } from '../config/env';

interface User {
  _id: string;
  name: string | null;
  email: string | null;
  phoneNumber?: string | null;
  location?: {
    type: string;
    coordinates: number[];
  };
  city?: string;
  state?: string;
  createdAt: string;
  authenticationMethod?: string;
}

const libraries: 'places'[] = ['places'];
const mapContainerStyle = {
  width: '100%',
  height: '600px',
};

const defaultCenter = {
  lat: 12.9716, // Bangalore default
  lng: 77.5946,
};

const UsersMapPage: React.FC = () => {
  const isStudio = apiService.isStudio();

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: ENV_CONFIG.GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [mapType, setMapType] = useState<'roadmap' | 'terrain' | 'satellite'>('roadmap');
  const [showUnknownUsersDialog, setShowUnknownUsersDialog] = useState(false);
  const [showCityUsersDialog, setShowCityUsersDialog] = useState(false);
  const [selectedCityUsers, setSelectedCityUsers] = useState<User[]>([]);
  const [selectedCityName, setSelectedCityName] = useState<string>('');

  const unknownUsers = useMemo(() => {
    return users.filter((user) => !user.city || user.city.trim() === '');
  }, [users]);

  const cityData = useMemo(() => {
    const cityMap = new Map<string, { count: number; users: User[] }>();
    
    users.forEach((user) => {
      const city = user.city || 'Unknown';
      if (!cityMap.has(city)) {
        cityMap.set(city, { count: 0, users: [] });
      }
      const data = cityMap.get(city)!;
      data.count++;
      data.users.push(user);
    });
    
    return Array.from(cityMap.entries())
      .map(([city, data]) => ({ city, ...data }))
      .sort((a, b) => {
        if (a.city === 'Unknown') return 1;
        if (b.city === 'Unknown') return -1;
        return b.count - a.count;
      });
  }, [users]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (users.length > 0 && isLoaded && window.google) {
      // Calculate map bounds to fit all markers
      const bounds = new window.google.maps.LatLngBounds();
      let hasLocations = false;
      
      const usersWithLocations = users.filter((u) => u.location?.coordinates && u.location.coordinates.length === 2);
      
      usersWithLocations.forEach((user) => {
        if (user.location?.coordinates && user.location.coordinates.length === 2) {
          bounds.extend({
            lat: user.location.coordinates[1],
            lng: user.location.coordinates[0],
          });
          hasLocations = true;
        }
      });
      
      if (hasLocations && map) {
        map.fitBounds(bounds);
        setFilteredUsers(usersWithLocations);
      } else {
        setFilteredUsers(usersWithLocations);
      }
    }
  }, [users, map, isLoaded]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = isStudio
        ? await apiService.getStudioUsersForMap()
        : await apiService.getUsers({});
      // Backend returns { success: true, data: users, total: number }
      const usersData = response.data || response.users || [];
      setUsers(usersData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (loadError) {
    return (
      <Box p={3}>
        <Alert severity="error">Error loading Google Maps. Please check your API key.</Alert>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleMarkerClick = (user: User) => {
    setSelectedUser(user);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) setMapType('roadmap');
    else if (newValue === 1) setMapType('terrain');
    else setMapType('satellite');
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700} mb={1}>
          {isStudio ? 'My Customers by Location' : 'Users by Location'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isStudio
            ? 'Visualize your customer distribution across different locations'
            : 'Visualize user distribution across different locations'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box 
        display="grid" 
        gridTemplateColumns={{ 
          xs: '1fr', 
          sm: 'repeat(2, 1fr)', 
          md: unknownUsers.length > 0 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          lg: unknownUsers.length > 0 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)'
        }} 
        gap={2} 
        mb={3}
      >
        <Card sx={{ border: `1px solid ${colors.divider}` }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Users
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {users.length}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: `${colors.primary}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Person sx={{ fontSize: 32, color: colors.primary }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ border: `1px solid ${colors.divider}` }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  With Location
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {filteredUsers.length}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: `${colors.secondary}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LocationOn sx={{ fontSize: 32, color: colors.secondary }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ border: `1px solid ${colors.divider}` }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Cities Covered
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {cityData.filter((c) => c.city !== 'Unknown').length}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: `${colors.info}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrendingUp sx={{ fontSize: 32, color: colors.info }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {unknownUsers.length > 0 && (
          <Card 
            sx={{ 
              border: `1px solid ${colors.warning || '#ff9800'}40`,
              cursor: 'pointer',
              '&:hover': {
                borderColor: colors.warning || '#ff9800',
                backgroundColor: `${colors.warning || '#ff9800'}08`,
              }
            }}
            onClick={() => setShowUnknownUsersDialog(true)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Unknown Location
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color={colors.warning || '#ff9800'}>
                    {unknownUsers.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Click to view details
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: `${colors.warning || '#ff9800'}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <HelpOutline sx={{ fontSize: 32, color: colors.warning || '#ff9800' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Map and Sidebar */}
      <Card sx={{ border: `1px solid ${colors.divider}`, overflow: 'hidden' }}>
        <Box display="flex">
          {/* Map */}
          <Box flex={1} position="relative" sx={{ minHeight: 600 }}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={10}
              onLoad={onMapLoad}
              onUnmount={onMapUnmount}
              options={{
                zoomControl: true,
                mapTypeControl: true,
                fullscreenControl: true,
                streetViewControl: false,
                mapTypeId: mapType,
              }}
            >
              {filteredUsers.map((user) => {
                if (!user.location?.coordinates || user.location.coordinates.length !== 2) return null;
                
                return (
                  <Marker
                    key={user._id}
                    position={{
                      lat: user.location.coordinates[1],
                      lng: user.location.coordinates[0],
                    }}
                    onClick={() => handleMarkerClick(user)}
                  />
                );
              })}

              {selectedUser && (
                <InfoWindow
                  position={{
                    lat: selectedUser.location!.coordinates[1],
                    lng: selectedUser.location!.coordinates[0],
                  }}
                  onCloseClick={() => setSelectedUser(null)}
                >
                  <Box p={1}>
                    <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                      {selectedUser.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      {selectedUser.email}
                    </Typography>
                    {selectedUser.city && (
                      <Chip
                        label={selectedUser.city}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </InfoWindow>
              )}
            </GoogleMap>

            {/* Map Type Controls */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'white',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="standard"
                sx={{ minHeight: 40 }}
              >
                <Tab label="Map" sx={{ minHeight: 40, fontSize: '0.75rem' }} />
                <Tab label="Terrain" sx={{ minHeight: 40, fontSize: '0.75rem' }} />
                <Tab label="Satellite" sx={{ minHeight: 40, fontSize: '0.75rem' }} />
              </Tabs>
            </Box>
          </Box>

          {/* City Stats Sidebar */}
          <Box sx={{ width: 320, borderLeft: `1px solid ${colors.divider}` }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.divider}` }}>
              <Typography variant="h6" fontWeight={600}>
                User Distribution
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Top cities by user count
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
              <List>
                {cityData.map((item, index) => (
                  <React.Fragment key={item.city}>
                    <ListItem
                      sx={{
                        py: 2,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: colors.backgroundTertiary,
                        },
                      }}
                      onClick={() => {
                        if (item.city === 'Unknown' && unknownUsers.length > 0) {
                          setShowUnknownUsersDialog(true);
                        } else if (item.city !== 'Unknown' && item.users.length > 0) {
                          setSelectedCityUsers(item.users);
                          setSelectedCityName(item.city);
                          setShowCityUsersDialog(true);
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: item.city === 'Unknown' 
                              ? `${colors.warning || '#ff9800'}20` 
                              : index < 3 
                                ? `${colors.primary}20` 
                                : `${colors.backgroundTertiary}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            color: item.city === 'Unknown'
                              ? colors.warning || '#ff9800'
                              : index < 3 
                                ? colors.primary 
                                : colors.textSecondary,
                          }}
                        >
                          {item.city === 'Unknown' ? '?' : index + 1}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {item.city}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.city === 'Unknown' 
                              ? `${unknownUsers.length} ${unknownUsers.length === 1 ? 'user' : 'users'} without location`
                              : `${item.count} ${item.count === 1 ? 'user' : 'users'}`}
                          </Typography>
                        </Box>
                        <Chip
                          label={item.city === 'Unknown' ? unknownUsers.length : item.count}
                          size="small"
                          sx={{
                            backgroundColor: item.city === 'Unknown'
                              ? `${colors.warning || '#ff9800'}15`
                              : `${colors.primary}15`,
                            color: item.city === 'Unknown'
                              ? colors.warning || '#ff9800'
                              : colors.primary,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </ListItem>
                    {index < cityData.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Box>
        </Box>
      </Card>

      <Dialog
        open={showUnknownUsersDialog}
        onClose={() => setShowUnknownUsersDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <HelpOutline sx={{ color: colors.warning || '#ff9800' }} />
            <Typography variant="h6" fontWeight={700}>
              Users with Unknown Location ({unknownUsers.length})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These users don't have a city/location set in their profile. Review their details below.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Auth Method</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unknownUsers.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: colors.primary,
                            fontSize: '0.875rem',
                          }}
                        >
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name || 'Anonymous'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {user.email || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {user.phoneNumber || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.authenticationMethod || 'Email'}
                        size="small"
                        sx={{
                          textTransform: 'capitalize',
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {new Date(user.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnknownUsersDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCityUsersDialog}
        onClose={() => setShowCityUsersDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOn sx={{ color: colors.primary }} />
            <Typography variant="h6" fontWeight={700}>
              Users in {selectedCityName} ({selectedCityUsers.length})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All users located in {selectedCityName}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Auth Method</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCityUsers.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: colors.primary,
                            fontSize: '0.875rem',
                          }}
                        >
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name || 'Anonymous'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {user.email || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {user.phoneNumber || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.authenticationMethod || 'Email'}
                        size="small"
                        sx={{
                          textTransform: 'capitalize',
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {new Date(user.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCityUsersDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersMapPage;

